import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  LinearProgress,
  TextField,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import {
  People,
  AttachMoney,
  Assignment,
  Receipt,
  Today,
  TrendingUp,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import invoiceService from '../services/invoice.service';
import { requestService } from '../services/requestService';
import employeeService from '../services/employee.service';
import paymentService from '../services/payment.service';
import authService from '../services/auth.service';
import ModuleLauncher from '../components/dashboard/ModuleLauncher';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');

  const [paymentsDateFrom, setPaymentsDateFrom] = useState<string>('');
  const [paymentsDateTo, setPaymentsDateTo] = useState<string>('');
  const [requestsDateFrom, setRequestsDateFrom] = useState<string>('');
  const [requestsDateTo, setRequestsDateTo] = useState<string>('');
  const [packageServicesDateFrom, setPackageServicesDateFrom] = useState<string>('');
  const [packageServicesDateTo, setPackageServicesDateTo] = useState<string>('');

  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalInvoices: 0,
    todayInvoices: 0,
    totalRequests: 0,
    todayRequests: 0,
    totalPayments: 0,
    todayPayments: 0,
    paymentsOverTime: [] as { month: string; amount: number }[],
    packageServicesData: [] as { name: string; value: number }[],
    requestsData: [] as { status: string; count: number }[],
  });

  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || user.email || '';
      setUserName(name);
    }
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];
        const todayStart = new Date(today).toISOString();
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        const todayEndISO = todayEnd.toISOString();

        const paymentFilters: Record<string, string | number> = {
          'pagination[pageSize]': 500,
          'sort[0]': 'paymentDate:desc',
        };
        if (paymentsDateFrom) paymentFilters['filters[paymentDate][$gte]'] = paymentsDateFrom;
        if (paymentsDateTo) paymentFilters['filters[paymentDate][$lte]'] = paymentsDateTo;

        const requestFilters: Record<string, string | number> = {
          'pagination[pageSize]': 500,
          'sort[0]': 'createdAt:desc',
        };
        if (requestsDateFrom) requestFilters['filters[createdAt][$gte]'] = requestsDateFrom;
        if (requestsDateTo) requestFilters['filters[createdAt][$lte]'] = requestsDateTo;

        const packageServicesFilters: Record<string, string | number> = {
          'pagination[pageSize]': 500,
          'sort[0]': 'createdAt:desc',
        };
        if (packageServicesDateFrom) packageServicesFilters['filters[createdAt][$gte]'] = packageServicesDateFrom;
        if (packageServicesDateTo) packageServicesFilters['filters[createdAt][$lte]'] = packageServicesDateTo;

        const [
          employeesResponse,
          invoicesResponse,
          invoicesTodayResponse,
          requestsResponse,
          requestsTodayResponse,
          paymentsResponse,
          paymentsTodayResponse,
          allPaymentsResponse,
          allRequestsResponse,
          packageServicesRequestsResponse,
        ] = await Promise.all([
          employeeService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          invoiceService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          invoiceService.getAll({
            'pagination[pageSize]': 1,
            'filters[createdAt][$gte]': todayStart,
            'filters[createdAt][$lte]': todayEndISO,
          }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          requestService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ meta: { pagination: { total: 0 } } })),
          requestService.getAll({
            'pagination[pageSize]': 1,
            'filters[createdAt][$gte]': todayStart,
            'filters[createdAt][$lte]': todayEndISO,
          }).catch(() => ({ meta: { pagination: { total: 0 } } })),
          paymentService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          paymentService.getAll({
            'pagination[pageSize]': 1,
            'filters[createdAt][$gte]': todayStart,
            'filters[createdAt][$lte]': todayEndISO,
          }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          paymentService.getAll(paymentFilters).catch(() => ({ data: { data: [] } })),
          requestService.getAll(requestFilters).catch(() => ({ data: [] })),
          requestService.getAll(packageServicesFilters).catch(() => ({ data: [] })),
        ]);

        const paymentsData = allPaymentsResponse.data?.data || [];
        const monthlyPayments: Record<string, number> = {};
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        let startDate: Date;
        let endDate: Date = new Date();
        if (paymentsDateFrom && paymentsDateTo) {
          startDate = new Date(paymentsDateFrom);
          endDate = new Date(paymentsDateTo);
        } else if (paymentsDateFrom) {
          startDate = new Date(paymentsDateFrom);
        } else if (paymentsDateTo) {
          startDate = new Date(endDate);
          startDate.setMonth(startDate.getMonth() - 5);
        } else {
          startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 5);
        }

        const monthsList: { key: string; display: string }[] = [];
        const current = new Date(startDate);
        current.setDate(1);
        while (current <= endDate) {
          const monthKey = `${monthNames[current.getMonth()]} ${current.getFullYear()}`;
          monthsList.push({ key: monthKey, display: monthNames[current.getMonth()] });
          monthlyPayments[monthKey] = 0;
          current.setMonth(current.getMonth() + 1);
        }

        paymentsData.forEach((payment: { attributes?: { paymentDate?: string; amount?: number }; paymentDate?: string; amount?: number; createdAt?: string }) => {
          const paymentDate = new Date(payment.attributes?.paymentDate || payment.paymentDate || payment.createdAt || '');
          const monthKey = `${monthNames[paymentDate.getMonth()]} ${paymentDate.getFullYear()}`;
          const amount = parseFloat(String(payment.attributes?.amount || payment.amount || 0));
          if (monthlyPayments[monthKey] !== undefined) {
            monthlyPayments[monthKey] += amount;
          }
        });

        const paymentsOverTime = monthsList.map((m) => ({
          month: m.display,
          amount: Math.round(monthlyPayments[m.key] || 0),
        }));

        const servicesMap = new Map<string, number>();
        const packageServicesRequestsData = packageServicesRequestsResponse.data || [];
        packageServicesRequestsData.forEach((request: { attributes?: { serviceType?: string }; serviceType?: string }) => {
          const serviceType = request.attributes?.serviceType || request.serviceType;
          if (serviceType) {
            servicesMap.set(serviceType, (servicesMap.get(serviceType) || 0) + 1);
          }
        });

        const packageServicesData = Array.from(servicesMap.entries()).map(([name, value]) => ({ name, value }));

        const statusCounts: Record<string, number> = {
          Pending: 0,
          Assigned: 0,
          Active: 0,
          'Invoice Creation': 0,
          'Invoice Sent': 0,
          'Payment Received': 0,
          Completed: 0,
        };

        const allRequestsData = allRequestsResponse.data || [];
        allRequestsData.forEach((request: { attributes?: { status?: string }; status?: string }) => {
          const status = request.attributes?.status || request.status || 'pending';
          if (status === 'pending') statusCounts.Pending++;
          else if (status === 'assigned') statusCounts.Assigned++;
          else if (status === 'active') statusCounts.Active++;
          else if (status === 'invoice_creation') statusCounts['Invoice Creation']++;
          else if (status === 'invoice_sent') statusCounts['Invoice Sent']++;
          else if (status === 'payment_received') statusCounts['Payment Received']++;
          else if (status === 'completed') statusCounts.Completed++;
        });

        const requestsData = Object.entries(statusCounts).map(([status, count]) => ({ status, count }));

        setStats({
          totalEmployees: employeesResponse.data?.meta?.pagination?.total || 0,
          totalInvoices: invoicesResponse.data?.meta?.pagination?.total || 0,
          todayInvoices: invoicesTodayResponse.data?.meta?.pagination?.total || 0,
          totalRequests: requestsResponse.meta?.pagination?.total || 0,
          todayRequests: requestsTodayResponse.meta?.pagination?.total || 0,
          totalPayments: paymentsResponse.data?.meta?.pagination?.total || 0,
          todayPayments: paymentsTodayResponse.data?.meta?.pagination?.total || 0,
          paymentsOverTime,
          packageServicesData,
          requestsData,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [paymentsDateFrom, paymentsDateTo, requestsDateFrom, requestsDateTo, packageServicesDateFrom, packageServicesDateTo]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];

  interface StatCardProps {
    title: string;
    value: number;
    subtitle?: string;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
  }

  const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color, onClick }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        cursor: onClick ? 'pointer' : 'default',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease',
        '&:hover': onClick
          ? { transform: 'translateY(-2px)', boxShadow: '0 8px 16px rgba(15,23,42,0.08)', borderColor: color }
          : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          {subtitle && (
            <Chip label={subtitle} size="small" color="primary" sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }} />
          )}
        </Box>
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t('navigation.dashboard')}
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Welcome header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          {t('dashboard.welcome', { name: userName || t('dashboard.user') })}
        </Typography>
      </Paper>

      {/* KPI strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 4,
        }}
      >
        <StatCard
          title={t('dashboard.totalEmployees')}
          value={stats.totalEmployees}
          icon={<People />}
          color="#8b5cf6"
          onClick={() => navigate('/employees')}
        />
        <StatCard
          title={t('dashboard.totalInvoices')}
          value={stats.totalInvoices}
          subtitle={`${t('common.today')}: ${stats.todayInvoices}`}
          icon={<Receipt />}
          color="#3b82f6"
          onClick={() => navigate('/invoices')}
        />
        <StatCard
          title={t('dashboard.totalRequests')}
          value={stats.totalRequests}
          subtitle={`${t('common.today')}: ${stats.todayRequests}`}
          icon={<Assignment />}
          color="#f59e0b"
          onClick={() => navigate('/requests')}
        />
        <StatCard
          title={t('dashboard.totalPayments')}
          value={stats.totalPayments}
          subtitle={`${t('common.today')}: ${stats.todayPayments}`}
          icon={<AttachMoney />}
          color="#10b981"
          onClick={() => navigate('/payments')}
        />
      </Box>

      {/* Odoo-style module launcher */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <ModuleLauncher />
      </Paper>

      {/* Analytics */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUp color="primary" />
        {t('dashboard.analytics')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              {t('dashboard.salesPaymentsOverTime')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                type="date"
                label={t('common.from')}
                value={paymentsDateFrom}
                onChange={(e) => setPaymentsDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                type="date"
                label={t('common.to')}
                value={paymentsDateTo}
                onChange={(e) => setPaymentsDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Button variant="outlined" size="small" onClick={() => { setPaymentsDateFrom(''); setPaymentsDateTo(''); }}>
                {t('common.reset')}
              </Button>
            </Box>
          </Box>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={stats.paymentsOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="amount" stroke="#6366f1" strokeWidth={2} name="Amount (OMR)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              {t('dashboard.packageServices')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                type="date"
                label={t('common.from')}
                value={packageServicesDateFrom}
                onChange={(e) => setPackageServicesDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                type="date"
                label={t('common.to')}
                value={packageServicesDateTo}
                onChange={(e) => setPackageServicesDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Button variant="outlined" size="small" onClick={() => { setPackageServicesDateFrom(''); setPackageServicesDateTo(''); }}>
                {t('common.reset')}
              </Button>
            </Box>
          </Box>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={stats.packageServicesData.length > 0 ? stats.packageServicesData : [{ name: 'No Data', value: 1 }]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats.packageServicesData.map((entry, index) => (
                    <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => `${value} requests`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            {t('dashboard.requestsByStatus')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              type="date"
              label={t('common.from')}
              value={requestsDateFrom}
              onChange={(e) => setRequestsDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              type="date"
              label={t('common.to')}
              value={requestsDateTo}
              onChange={(e) => setRequestsDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button variant="outlined" size="small" onClick={() => { setRequestsDateFrom(''); setRequestsDateTo(''); }}>
              {t('common.reset')}
            </Button>
          </Box>
        </Box>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.requestsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#6366f1" name="Number of Requests" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
        <StatCard
          title={t("Today's Invoices")}
          value={stats.todayInvoices}
          icon={<Today />}
          color="#6366f1"
          onClick={() => {
            const today = new Date().toISOString().split('T')[0];
            navigate(`/invoices?dateFrom=${today}&dateTo=${today}`);
          }}
        />
        <StatCard
          title={t("Today's Requests")}
          value={stats.todayRequests}
          icon={<Today />}
          color="#ef4444"
          onClick={() => {
            const today = new Date().toISOString().split('T')[0];
            navigate(`/requests?dateFrom=${today}&dateTo=${today}`);
          }}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;
